import axios from 'axios'
import { addMinutes, startOfMinute } from 'date-fns'
import { GRAPH_CONFIG } from '../config/graphConfig'
import {
  clipSlotsToBusinessHours,
  clipSlotsToHours,
  dedupeSlots,
  generateSlotsFromAvailability,
  parseDurationToMinutes,
  sortSlots,
} from '../utils/dateTime'
import type {
  AvailabilityRequest,
  AvailableSlot,
  BookingAppointment,
  BookingAppointmentPayload,
  BookingBusinessInfo,
  BookingService,
  BookingStaffMember,
  SchedulingPolicy,
  StaffAvailabilityItem,
} from '../types/bookings'

interface GraphCollection<T> {
  value: T[]
}

interface GraphBookingService {
  id: string
  displayName: string
  description: string | null
  defaultDuration?: string | null
  preBuffer?: string | null
  postBuffer?: string | null
  defaultPrice?: number | null
  defaultPriceType?:
    | 'undefined'
    | 'fixedPrice'
    | 'startingAt'
    | 'hourly'
    | 'free'
    | 'priceVaries'
    | 'callUs'
    | 'notSet'
    | 'unknownFutureValue'
    | null
  staffMemberIds?: string[] | null
  isLocationOnline: boolean
  isCustomerAllowedToManageBooking?: boolean
  defaultLocation?: BookingService['defaultLocation']
  customQuestions?: BookingService['customQuestions']
  schedulingPolicy?: SchedulingPolicy | null
  isHiddenFromCustomers?: boolean
  smsNotificationsEnabled?: boolean
  maximumAttendeesCount?: number | null
}

const api = axios.create({
   baseURL: GRAPH_CONFIG.apiBase,
  //baseURL:'https://localhost:7162/api/bookings/'
})

function extractGraphError(error: unknown): never {
  if (axios.isAxiosError(error)) {
    const graphMessage =
      error.response?.data?.error?.message ??
      error.response?.data?.message ??
      error.message
    throw new Error(graphMessage)
  }

  throw error
}

export async function getBookingServices(): Promise<BookingService[]> {
  try {
    const response = await api.get<GraphBookingService[]>('/services')

    return response.data
      .map((service) => ({
        id: service.id,
        displayName: service.displayName,
        description: service.description,
        duration: service.defaultDuration ?? 'PT30M',
        preBuffer: service.preBuffer,
        postBuffer: service.postBuffer,
        price: service.defaultPrice ?? null,
        priceType: service.defaultPriceType ?? 'notSet',
        staffMemberIds: service.staffMemberIds ?? [],
        isLocationOnline: service.isLocationOnline,
        isCustomerAllowedToManageBooking: service.isCustomerAllowedToManageBooking,
        defaultLocation: service.defaultLocation ?? null,
        customQuestions: service.customQuestions ?? [],
        schedulingPolicy: service.schedulingPolicy
          ? {
              timeSlotInterval: service.schedulingPolicy.timeSlotInterval,
              minimumLeadTime: service.schedulingPolicy.minimumLeadTime,
              maximumAdvance: service.schedulingPolicy.maximumAdvance,
              sendConfirmationsToOwner: service.schedulingPolicy.sendConfirmationsToOwner,
              allowStaffSelection: service.schedulingPolicy.allowStaffSelection,
              generalAvailability: service.schedulingPolicy.generalAvailability,
              customAvailabilities: service.schedulingPolicy.customAvailabilities,
            }
          : null,
        isHiddenFromCustomers: service.isHiddenFromCustomers,
        smsNotificationsEnabled: service.smsNotificationsEnabled,
        maximumAttendeesCount: service.maximumAttendeesCount ?? null,
      }))
      .filter((service) => !service.isHiddenFromCustomers)
  } catch (error) {
    extractGraphError(error)
  }
}

export async function getStaffMembers(): Promise<BookingStaffMember[]> {
  try {
    const response = await api.get<BookingStaffMember[]>('/staff-members')

    return response.data
  } catch (error) {
    extractGraphError(error)
  }
}

export async function getBusinessInfo(): Promise<BookingBusinessInfo> {
  try {
    const response = await api.get<BookingBusinessInfo>('/business-info')
    return {
      timeZone: response.data.timeZone ?? 'UTC',
      businessHours: response.data.businessHours ?? [],
    }
  } catch (error) {
    extractGraphError(error)
  }
}

export async function getStaffAvailability(
  payload: AvailabilityRequest,
  service: BookingService,
  staffMembers: BookingStaffMember[],
  businessInfo: BookingBusinessInfo | null,
  selectedStaffId?: string | null,
): Promise<AvailableSlot[]> {
  try {
    const response = await api.post<GraphCollection<StaffAvailabilityItem> | StaffAvailabilityItem[]>(
      '/availability',
      payload,
    )

    const availabilityItems = Array.isArray(response.data)
      ? response.data
      : response.data.value

    const staffMap = new Map(staffMembers.map((staff) => [staff.id, staff]))
    const generated = availabilityItems.flatMap((item) => {
      const staff = staffMap.get(item.staffId)
      if (!staff) {
        return []
      }

      let slots = generateSlotsFromAvailability({
        availability: item.availabilityItems,
        service,
        staff,
        timeZone: payload.startDateTime.timeZone,
      })

      if (staff.useBusinessHours === false && staff.workingHours?.length) {
        const staffTz = staff.timeZone || businessInfo?.timeZone || 'UTC'
        slots = clipSlotsToHours(slots, staff.workingHours, staffTz)
      }

      return slots
    })

    const clipped = businessInfo
      ? clipSlotsToBusinessHours(generated, businessInfo)
      : generated

    const serviceHours = service.schedulingPolicy?.generalAvailability?.businessHours ?? []
    const serviceClipped =
      businessInfo &&
      service.schedulingPolicy?.generalAvailability?.availabilityType === 'customWeeklyHours' &&
      serviceHours.length
        ? clipSlotsToHours(clipped, serviceHours, businessInfo.timeZone)
        : clipped

    const leadMinutes = service.schedulingPolicy?.minimumLeadTime
      ? parseDurationToMinutes(service.schedulingPolicy.minimumLeadTime)
      : 0
    const earliestBookable = startOfMinute(addMinutes(new Date(), leadMinutes))
    const futureSlots = serviceClipped.filter((slot) => slot.startDateTime >= earliestBookable)

    const filtered = selectedStaffId
      ? futureSlots.filter((slot) => slot.staffId === selectedStaffId)
      : dedupeSlots(futureSlots)

    return sortSlots(filtered)
  } catch (error) {
    extractGraphError(error)
  }
}

export async function createAppointment(payload: BookingAppointmentPayload): Promise<BookingAppointment> {
  try {
    const response = await api.post<BookingAppointment>('/appointments', payload)

    return response.data
  } catch (error) {
    extractGraphError(error)
  }
}
